import React, { useEffect, useState } from "react";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {
  Alert,
  Box,
  Button,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { EDIT_USER } from "../graphql/userGraphql";
import { useTranslation } from "../context/Internationalization";
import { Modal, ModalActions } from "./Modal";
import ModalBody from "./Modal/ModalBody";
import ModalTitle from "./Modal/ModalTitle";

export const parseContactPhones = (value) =>
  Array.from(
    new Set(
      value
        .split(/[,\n]+/)
        .map((phone) => phone.trim())
        .filter(Boolean)
    )
  );

const ContactPhoneModal = ({
  open,
  onClose,
  user,
  axiosGraphQL,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setValue((user?.contactInfo?.phones || []).join(", "));
      setStatus("idle");
      setError("");
    }
  }, [open, user?.contactInfo?.phones]);

  const close = () => {
    if (status === "pending") return;
    setStatus("idle");
    setError("");
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (status === "pending") return;

    setStatus("pending");
    setError("");
    try {
      await axiosGraphQL({
        query: EDIT_USER,
        variables: { contactPhones: parseContactPhones(value) },
      });
      await onCompleted();
      onClose();
    } catch {
      setError(t("contact_phone_save_failed"));
      setStatus("idle");
    }
  };

  const title = t("edit_contact_phone");

  return (
    <Modal
      open={open}
      onClose={close}
      type="info"
      ariaLabel={title}
      closeLabel={t("close")}
    >
      <form onSubmit={submit}>
        <ModalTitle title={title} />
        <ModalBody>
          <Box sx={{ width: "100%" }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              label={t("contact_phone")}
              helperText={t("contact_phone_helper")}
              value={value}
              disabled={status === "pending"}
              onChange={(event) => setValue(event.target.value)}
            />
          </Box>
        </ModalBody>
        <ModalActions>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={close}
            disabled={status === "pending"}
          >
            {t("cancel")}
          </Button>
          <LoadingButton
            startIcon={<CheckOutlinedIcon />}
            loadingPosition="start"
            type="submit"
            fullWidth
            loading={status === "pending"}
          >
            {t("save")}
          </LoadingButton>
        </ModalActions>
      </form>
    </Modal>
  );
};

export default ContactPhoneModal;
